import { useEffect, useState } from 'react';
import { Button, Chip, Field, Icon, Panel, Select, TextInput, Toggle, type ChipTone } from '../../components/ui';
import { request, errorMessage } from '../../lib/api';
import { ProviderKeyPanel, paceText, testMessage } from './ProviderKeyPanel';
import { clearProviderCache } from '../../features/setup/providers';
import { Disclosure, Notice } from './pageParts';

interface AdditionalStatus {
  aeroApiConfigured?: boolean;
  aeroApiEnabled?: boolean;
  configured: boolean;
  enabled: boolean;
  priority: number;
  maxPerHour: number;
  maxPerDay: number;
  source: 'personal' | 'organization';
  lastResult: { state: string; httpStatus: number | null } | null;
}

/** "Enabled" / "Saved, off" / "Not set up" as a chip for a provider row. */
function sourceChip(configured: boolean | undefined, enabled: boolean | undefined): { label: string; tone: ChipTone } {
  if (!configured) return { label: 'Not set up', tone: 'neutral' };
  return enabled ? { label: 'Enabled', tone: 'live' } : { label: 'Saved, off', tone: 'neutral' };
}

/** Provider setup uses fixed supported adapters, never arbitrary URLs. Keys are write-only. */
export function DataSourcesPanel({ organizationId, canManage = true }: { organizationId?: string; canManage?: boolean } = {}) {
  const endpoint = organizationId ? `/organizations/${organizationId}/data-sources/adsbexchange` : '/me/data-sources/adsbexchange';
  const [status, setStatus] = useState<AdditionalStatus | null>(null);
  const [key, setKey] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [priority, setPriority] = useState(1);
  const [hour, setHour] = useState(30);
  const [day, setDay] = useState(100);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const apply = (s: AdditionalStatus) => {
    setStatus(s);
    setEnabled(s.configured ? s.enabled : true);
    setPriority(s.priority);
    setHour(s.maxPerHour);
    setDay(s.maxPerDay);
  };
  useEffect(() => {
    let active = true;
    const load = () =>
      request<AdditionalStatus>('GET', endpoint)
        .then((s) => {
          if (active) apply(s);
        })
        .catch((e) => {
          if (active) setError(errorMessage(e));
        });
    void load();
    window.addEventListener('sightline:provider-settings', load);
    return () => {
      active = false;
      window.removeEventListener('sightline:provider-settings', load);
    };
  }, [endpoint]);

  const changed = () => {
    clearProviderCache();
    window.dispatchEvent(new Event('sightline:provider-settings'));
  };
  async function save() {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      apply(await request<AdditionalStatus>('PUT', endpoint, { ...(key.trim() ? { key: key.trim() } : {}), enabled, priority, maxPerHour: hour, maxPerDay: day }));
      setKey('');
      changed();
      setMessage('Source saved. Saving makes no provider request. Tracking will show the result of its first request.');
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }
  async function test() {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const r = await request<{ lastResult: AdditionalStatus['lastResult']; status: AdditionalStatus }>('POST', `${endpoint}/test`);
      setStatus(r.status);
      changed();
      (r.lastResult?.state === 'ok' ? setMessage : setError)(testMessage('ADS-B Exchange', r.lastResult));
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }
  async function remove() {
    if (!window.confirm('Remove this ADS-B Exchange key? Tracking will use your remaining enabled sources.')) return;
    setBusy(true);
    setError('');
    try {
      apply(await request<AdditionalStatus>('DELETE', endpoint));
      setKey('');
      changed();
      setMessage('Source removed.');
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  const inherited = !organizationId && status?.source === 'organization';
  const aero = sourceChip(status?.aeroApiConfigured, status?.aeroApiEnabled);
  const adsb = sourceChip(status?.configured, status?.enabled);
  const invalidCaps = !Number.isInteger(hour) || hour < 1 || hour > 120 || !Number.isInteger(day) || day < 1 || day > 1000;
  const adsbState = !status
    ? 'Loading sources…'
    : !status.configured
      ? 'No key saved'
      : !status.enabled
        ? 'Saved · off'
        : status.lastResult?.state === 'ok'
          ? 'Last request accepted'
          : status.lastResult
            ? 'Last request failed or was limited · other sources are tried automatically'
            : 'Enabled · not tested yet';

  return (
    <section className="data-sources" aria-label="Aircraft data sources">
      <Panel title="Set up aircraft data" eyebrow={organizationId ? 'Organization' : 'Aircraft data'}>
        <div className="panel-stack">
          <p className="panel-lede">
            Airview Studio is free and does not supply aircraft data. Live positions come from a data account you (or your organization) already have with one of the providers below. Add either or both; enabled sources are tried in your chosen order when one fails, is limited, or has no recent position.
          </p>
          <Disclosure summary="How to set up a data key (about 5 minutes)" open={!!status && !status.configured && !status.aeroApiConfigured && !inherited}>
            <ol className="source-guide">
              <li>
                <strong>Get a key.</strong> FlightAware AeroAPI is the simplest: open{' '}
                <a href="https://www.flightaware.com/commercial/aeroapi/" target="_blank" rel="noreferrer">
                  FlightAware AeroAPI
                </a>
                , sign in to your FlightAware account and create an API key on the AeroAPI page. A FlightAware website password is not an API key.
              </li>
              <li>
                <strong>ADS-B Exchange:</strong> open{' '}
                <a href="https://www.adsbexchange.com/products/enterprise-api/" target="_blank" rel="noreferrer">
                  Enterprise API access
                </a>{' '}
                and obtain a key for the{' '}
                <a href="https://gateway.adsbexchange.com/api/aircraft/v2/docs" target="_blank" rel="noreferrer">
                  v2 gateway API
                </a>
                . Community and RapidAPI keys use different services and will not work in this field.
              </li>
              <li>
                <strong>Paste it below.</strong> Open the matching provider, paste the key, keep the suggested request caps and press Save. Keys are encrypted and never shown again, to you or your crew.
              </li>
              <li>
                <strong>Press Test key.</strong> You see straight away whether the provider accepts it. The FlightAware test uses its free account check; the ADS-B Exchange test uses one request from your caps.
              </li>
            </ol>
            <p className="muted small">
              Your data account is between you and the provider, under its own terms; check they permit your use (for a shared organization, team use). Without a key nothing is tracked, and practice missions need no key.
            </p>
          </Disclosure>
          {error && !status ? (
            <Notice tone="error" role="alert">
              {error}
            </Notice>
          ) : null}
        </div>
      </Panel>

      <div className="source-list">
        <Disclosure
          variant="card"
          className="data-source-card"
          summary={<SourceSummary icon="aircraft" name="FlightAware AeroAPI" chip={aero} />}
        >
          <ProviderKeyPanel organizationId={organizationId} canManage={canManage} hasOtherSource={!!status?.enabled} multiSource />
        </Disclosure>
        <Disclosure variant="card" className="data-source-card" summary={<SourceSummary icon="signal" name="ADS-B Exchange" chip={adsb} />}>
          {inherited || !canManage ? (
            <Panel title="Organization fallback sources" variant="flat">
              <p>
                Your organization admins manage source order and credentials.{' '}
                {status?.configured
                  ? status.enabled
                    ? 'An ADS-B Exchange source is enabled.'
                    : 'An ADS-B Exchange source is saved but off.'
                  : 'No ADS-B Exchange source is configured.'}
              </p>
            </Panel>
          ) : (
            <Panel title="ADS-B Exchange · additional source" variant="flat">
              <form
                className="panel-stack"
                onSubmit={(e) => {
                  e.preventDefault();
                  void save();
                }}
              >
                <p className="muted">
                  Optional: use this as your first source or a fallback for AeroAPI. Either provider works on its own. Your existing AeroAPI key and this key share the caps below. No shared free feeds are added.
                </p>
                <div className="source-state" role="status">
                  <Icon name={status?.configured && status.enabled ? 'signal' : 'info'} size={15} />
                  <span>{adsbState}</span>
                </div>
                <Field
                  label={status?.configured ? 'Replace ADS-B Exchange key (optional)' : 'ADS-B Exchange Enterprise API key'}
                  hint="Use your gateway v2 API key. Leave blank to keep a saved key."
                >
                  {(id) => <TextInput id={id} type="password" mono autoComplete="off" value={key} maxLength={256} spellCheck={false} onChange={(e) => setKey(e.target.value)} />}
                </Field>
                <Field label="Provider order">
                  {(id) => (
                    <Select id={id} value={priority} onChange={(e) => setPriority(Number(e.target.value))}>
                      <option value={1}>AeroAPI first → ADS-B Exchange</option>
                      <option value={0}>ADS-B Exchange first → AeroAPI</option>
                    </Select>
                  )}
                </Field>
                <div className="settings-grid">
                  <Field label="Requests per aircraft per hour" hint={`1–120 · ${paceText(hour)}. Failed attempts and fallback requests count.`}>
                    {(id) => <TextInput id={id} type="number" mono min={1} max={120} value={hour} onChange={(e) => setHour(Number(e.target.value))} />}
                  </Field>
                  <Field label="Shared request cap per 24 hours" hint="1–1000 across both providers, shoots, fleet aircraft and paid traffic. Provider charges vary; this is a request cap, not a dollar cap.">
                    {(id) => <TextInput id={id} type="number" mono min={1} max={1000} value={day} onChange={(e) => setDay(Number(e.target.value))} />}
                  </Field>
                </div>
                <Toggle label="Enable ADS-B Exchange" checked={enabled} disabled={busy} onChange={setEnabled} />
                {error ? (
                  <Notice tone="error" role="alert">
                    {error}
                  </Notice>
                ) : null}
                {message ? (
                  <Notice tone="success" role="status">
                    {message}
                  </Notice>
                ) : null}
                <div className="panel-actions">
                  {status?.configured ? (
                    <Button variant="ghost" icon="trash" disabled={busy} onClick={() => void remove()}>
                      Remove ADS-B Exchange key
                    </Button>
                  ) : null}
                  {status?.configured ? (
                    <Button variant="secondary" icon="signal" disabled={busy} onClick={() => void test()}>
                      Test key
                    </Button>
                  ) : null}
                  <Button type="submit" variant="primary" loading={busy} disabled={!status || (!status.configured && !key.trim()) || invalidCaps}>
                    Save data source
                  </Button>
                </div>
              </form>
            </Panel>
          )}
        </Disclosure>
      </div>
    </section>
  );
}

/** Provider row summary. The hidden " · " keeps the summary text "Name · state" for assistive tech and tests. */
function SourceSummary({ icon, name, chip }: { icon: 'aircraft' | 'signal'; name: string; chip: { label: string; tone: ChipTone } }) {
  return (
    <span className="source-summary">
      <span className="source-summary__icon" aria-hidden>
        <Icon name={icon} size={18} />
      </span>
      <span className="source-summary__name">{name}</span>
      <span className="sr-only"> · </span>
      <Chip size="sm" tone={chip.tone} dot={chip.tone === 'live'}>
        {chip.label}
      </Chip>
    </span>
  );
}
